import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, View } from 'react-native'
import { SVGGroup } from '@pff-consumer/utils/lib/svg/svg-group'
import {
  TOAST_ANIMATION_DURATION,
  TOAST_OPEN_CLOSE_DURATION,
  TOAST_CONTAINER_WIDTH,
} from '@pff-consumer/schema/common/form'
import { colors } from '@pff-consumer/core-ui'
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { Text, TextDescriptionVariant } from '../text'
import { useStyles } from './toast.styles'
import { Icon } from '../icon/icon'
import { ToastProps } from './toast.props'
import { PressableOpacity } from '../pressable-opacity/pressable-opacity'
import { useBreakpoints, useDimensions } from '../../styles/use-dimensions'

const TOAST_ICON_SIZE = 12
const CLOSE_ICON_SIZE = 16

export const Toast = (props: ToastProps) => {
  const { showToast, hasErrored, onClose } = props
  const { screenDimensions } = useDimensions()
  const { isDesktop } = useBreakpoints()
  const isWeb = Platform.OS === 'web'
  const toastWidth = isWeb ? TOAST_CONTAINER_WIDTH : screenDimensions.width

  const [openToast, setOpenToast] = useState(showToast)

  // toast open/close animation
  const isVisible = useSharedValue(openToast)
  const toastHeight = useSharedValue(0)
  const toastVisibility = useDerivedValue(
    () =>
      withTiming(isVisible.value ? 0 : 1, { duration: TOAST_OPEN_CLOSE_DURATION, easing: Easing.inOut(Easing.quad) }),
    [isVisible]
  )
  const openToastAnimatedStyle = useAnimatedStyle(
    () => ({
      transform: [{ translateY: toastVisibility.value * 2 * toastHeight.value }],
    }),
    [toastVisibility, toastHeight]
  )

  // progress bar animation
  const progressBarWidth = useSharedValue(0)
  const animatedStyles = useAnimatedStyle(() => {
    return {
      width: progressBarWidth.value,
    }
  }, [progressBarWidth])

  const styles = useStyles({
    hasErrored,
    isDesktop,
    toastWidth,
  })

  // Use discriminated union narrowing
  const { errorMessage, successfulMessage } = props
  const text = hasErrored ? errorMessage : successfulMessage
  const iconName = hasErrored ? 'info-white' : 'circle-checkmark-white'

  const handleClose = useCallback(() => {
    isVisible.value = false
    // handle close only after the close animation ends
    setTimeout(() => {
      onClose()
      setOpenToast(false)
    }, TOAST_OPEN_CLOSE_DURATION)
  }, [onClose])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (showToast) {
      setOpenToast(true)
      isVisible.value = true
      progressBarWidth.value = withTiming(toastWidth, {
        duration: TOAST_ANIMATION_DURATION,
        easing: Easing.in(Easing.quad),
        reduceMotion: ReduceMotion.System,
      })
      timer = setTimeout(() => {
        handleClose()
      }, TOAST_ANIMATION_DURATION)
    }
    return () => {
      clearTimeout(timer)
      isVisible.value = openToast
      toastHeight.value = 0
      progressBarWidth.value = 0
    }
  }, [showToast])

  if (!text) {
    console.warn('Toast: No message provided. Either errorMessage or successfulMessage is required.')
    return null
  }

  if (!openToast) {
    return null
  }

  return (
    <Animated.View
      onLayout={(e) => {
        toastHeight.value = e.nativeEvent.layout.height
      }}
      style={[isWeb ? styles.toastContainerWeb : styles.toastContainer, openToastAnimatedStyle]}
      testID='toast'
    >
      <View style={styles.toastSubContainer}>
        <View style={styles.toastMessageText}>
          <Icon
            name={iconName}
            group={SVGGroup.Icons}
            height={TOAST_ICON_SIZE}
            width={TOAST_ICON_SIZE}
          />
          <Text
            variant={TextDescriptionVariant.TEXT_SMALL_REGULAR}
            colorOverride={colors.lightTextColor}
            testID='toastMessage'
          >
            {text}
          </Text>
        </View>
        <PressableOpacity
          onPressHandler={handleClose}
          style={styles.closeButton}
          testID='closeButton'
        >
          <Icon
            name='close'
            group={SVGGroup.Icons}
            height={CLOSE_ICON_SIZE}
            width={CLOSE_ICON_SIZE}
          />
        </PressableOpacity>

        <Animated.View
          testID='toast.progressBar'
          style={[styles.progressBar, animatedStyles]}
        />
      </View>
    </Animated.View>
  )
}
